import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { STELE_SPACE_KIND, STELE_CLIENT, spaceDTag, slugify, isValidSlug } from '@/lib/stele';
import { Loader2 } from 'lucide-react';

interface CreateSpaceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EMOJI_OPTIONS = ['📖', '📚', '🔧', '💡', '🚀', '🎯', '📝', '🔬', '🎨', '🏗️', '⚡', '🌟'];

export function CreateSpaceDialog({ open, onOpenChange }: CreateSpaceDialogProps) {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('📖');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const { mutateAsync: createEvent, isPending } = useNostrPublish();
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (!slugManuallyEdited) {
      setSlug(slugify(value));
    }
  };

  const handleSlugChange = (value: string) => {
    setSlugManuallyEdited(true);
    setSlug(value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
  };

  const canSubmit = title.trim() && isValidSlug(slug) && user;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    await createEvent({
      kind: STELE_SPACE_KIND,
      content: '',
      tags: [
        ['d', spaceDTag(slug)],
        ['title', title.trim()],
        ['description', description.trim()],
        ['icon', icon],
        ['alt', `Stele documentation space: ${title.trim()}`],
        ['client', STELE_CLIENT],
      ],
    });

    await queryClient.invalidateQueries({ queryKey: ['nostr', 'stele'] });
    onOpenChange(false);
    setTitle('');
    setSlug('');
    setDescription('');
    setIcon('📖');
    setSlugManuallyEdited(false);
    navigate(`/s/${slug}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Space</DialogTitle>
          <DialogDescription>
            A space is a documentation project. It holds pages organized in a tree.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="space-icon">Icon</Label>
            <div className="flex flex-wrap gap-2">
              {EMOJI_OPTIONS.map(e => (
                <button
                  key={e}
                  type="button"
                  className={`flex h-9 w-9 items-center justify-center rounded-lg text-lg transition-all ${
                    icon === e
                      ? 'bg-primary/10 ring-2 ring-primary'
                      : 'bg-muted hover:bg-muted/80'
                  }`}
                  onClick={() => setIcon(e)}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="space-title">Title</Label>
            <Input
              id="space-title"
              placeholder="My Documentation"
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="space-slug">Slug</Label>
            <Input
              id="space-slug"
              placeholder="my-documentation"
              value={slug}
              onChange={(e) => handleSlugChange(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Used in URLs: /r/npub.../<strong>{slug || 'slug'}</strong>
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="space-description">Description</Label>
            <Textarea
              id="space-description"
              placeholder="What is this space about?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit || isPending}>
              {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Space
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
